import {useApolloClient} from '@apollo/client';
import {Option, none, some} from '@danwang/option';
import {
  useAllAccountsQuery,
  useBindingsQuery,
  useCreateRawTransactionsMutation,
  useSetBindingMutation,
} from '@transactions/graphql';
import {BINDINGS} from 'common/bindings';
import {ChevronRight} from 'lucide-react';
import * as React from 'react';
import AccountSelect from 'src/AccountSelect';
import {isAccountClosed} from 'src/AccountsView/accountFilter';
import {Button} from 'src/Button';
import {ImportEzpass} from 'src/ImportEzpass';
import {ImportVenmo} from 'src/ImportVenmo';
import InputGroup from 'src/InputGroup';
import {LogoImage} from 'src/LogoImage';
import {Modal} from 'src/Modal';
import type {FetchResult} from '@apollo/client';
import type {
  AccountListItemFragment,
  BasicRawTransactionFragment,
  CreateRawTransactionsMutation,
} from '@transactions/graphql';
import type {BindingKey} from 'common/bindings';
import type {LogoId} from 'src/logoOptions';
import type {SystemProps} from 'src/Modals';
import type {EzpassResult} from 'src/parseEzpass';
import type {VenmoResult} from 'src/parseVenmo';

export type ImportTransactionsModalUserProps = {
  onImport?: (created: BasicRawTransactionFragment[]) => void;
};

type ImportType = 'venmo' | 'ezpass';
type TransactionResult = VenmoResult | EzpassResult;

// The binding slot remembering each source's destination account — imports
// from one source always target the same account, so the picker prefills
// from the binding and the chosen account is written back after a
// successful import. See common/bindings.ts.
const BINDING_KEYS: Record<ImportType, BindingKey> = {
  venmo: 'venmoImportAccount',
  ezpass: 'ezpassImportAccount',
};

const SOURCES: Record<
  ImportType,
  {name: string; logo: LogoId; description: string}
> = {
  venmo: {
    name: 'Venmo',
    logo: 'venmo',
    description: 'Payments and transfers, pasted as JSON from venmo.com',
  },
  ezpass: {
    name: 'E-ZPass NY',
    logo: 'ezpass',
    description: 'Tolls and replenishments, pasted as JSON from your account',
  },
};

const SourceButton = ({
  source,
  onClick,
}: {
  source: ImportType;
  onClick: () => void;
}) => {
  const {name, logo, description} = SOURCES[source];
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-input hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-left transition"
    >
      <LogoImage logo={logo} className="size-8" />
      <div className="min-w-0 flex-1">
        <div className="text-foreground">{name}</div>
        <div className="text-muted-foreground text-sm">{description}</div>
      </div>
      <ChevronRight className="text-muted-foreground size-4 shrink-0" />
    </button>
  );
};

export const ImportTransactionsModal = (
  props: ImportTransactionsModalUserProps & SystemProps,
) => {
  const {onImport, onRequestClose} = props;
  const client = useApolloClient();
  const [importType, setImportType] = React.useState<ImportType | null>(null);
  const [account, setAccount] =
    React.useState<Option<AccountListItemFragment>>(none);
  const [createRawTransactions, {loading}] = useCreateRawTransactionsMutation();
  const [setBinding] = useSetBindingMutation();
  const [parsed, setParsed] = React.useState<Option<TransactionResult[]>>(none);

  const {data: bindingsData} = useBindingsQuery();
  // Same query AccountSelect runs, so this is served from the cache; used to
  // resolve the bound account id to the fragment the picker works with.
  const {data: accountsData} = useAllAccountsQuery();

  // Prefill the account from the source's remembered binding, once per
  // source selection — after seeding, the field belongs to the user (a
  // cleared picker must not snap back).
  const [seededType, setSeededType] = React.useState<ImportType | null>(null);
  React.useEffect(() => {
    if (importType == null || seededType === importType) return;
    if (bindingsData == null || accountsData == null) return;
    setSeededType(importType);
    const boundId = bindingsData.bindings.find(
      (binding) => binding.key === BINDING_KEYS[importType],
    )?.entityId;
    const spec = BINDINGS[BINDING_KEYS[importType]];
    const bound = accountsData.allAccounts.find(
      // A bound account that has since closed accepts no new rows, and one
      // in the wrong currency would fail the server's account-currency
      // guard — leave the picker empty rather than prefill a doomed choice.
      (candidate) =>
        candidate._id === boundId &&
        !isAccountClosed(candidate) &&
        (spec.currency == null || candidate.currency === spec.currency),
    );
    if (bound && account.isEmpty) {
      setAccount(some(bound));
    }
  }, [importType, seededType, bindingsData, accountsData, account]);

  // Remember the destination for next time. The import has already
  // succeeded, so a failure here is not worth surfacing to the user.
  const rememberAccount = (type: ImportType, accountId: string) => {
    const key = BINDING_KEYS[type];
    const current = bindingsData?.bindings.find(
      (binding) => binding.key === key,
    )?.entityId;
    if (current === accountId) return;
    setBinding({variables: {key, entityId: accountId}})
      .then(() => {
        client.cache.evict({fieldName: 'bindings'});
      })
      .catch(() => {});
  };

  const handleVenmoChange = (value: Option<VenmoResult[]>) => {
    setParsed(value);
  };

  const handleEzpassChange = (value: Option<EzpassResult[]>) => {
    setParsed(value);
  };

  const handleSubmit = () => {
    if (importType == null) return;
    Option.allObject({
      account,
      transactions: parsed,
    }).forEach(({account, transactions}) => {
      createRawTransactions({
        variables: {
          transactions: transactions.map((t) => ({
            ...t,
            currency: 'USD',
            accountId: account._id,
          })),
        },
      }).then((result: FetchResult<CreateRawTransactionsMutation>) => {
        const newObjects = result?.data?.createRawTransactions;
        if (newObjects) {
          client.cache.evict({fieldName: 'existingExternalIds'});
          rememberAccount(importType, account._id);
          onRequestClose();
          onImport?.(newObjects);
        }
      });
    });
  };

  // Step 1: Select import type
  if (importType === null) {
    return (
      <Modal onRequestClose={onRequestClose} header="Import transactions">
        <div className="flex flex-col gap-2">
          <SourceButton source="venmo" onClick={() => setImportType('venmo')} />
          <SourceButton
            source="ezpass"
            onClick={() => setImportType('ezpass')}
          />
        </div>
      </Modal>
    );
  }

  // Step 2: Import data
  const footer = (
    <>
      <Button
        variant="ghost"
        className="mr-auto"
        onClick={() => {
          setImportType(null);
          setParsed(none);
          setAccount(none);
          // Re-arm the prefill: re-entering a source should seed the
          // just-cleared picker from its binding again.
          setSeededType(null);
        }}
        disabled={loading}
      >
        Back
      </Button>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={parsed.isEmpty || account.isEmpty || loading}
        onClick={handleSubmit}
      >
        Import
      </Button>
    </>
  );

  const header = (
    <div className="flex items-center gap-2">
      <LogoImage logo={SOURCES[importType].logo} className="size-5" />
      Import from {SOURCES[importType].name}
    </div>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header={header}
      footer={footer}
      className="sm:w-2xl"
    >
      <div className="flex flex-col gap-4">
        <InputGroup label="Account">
          <AccountSelect
            selectedAccount={account}
            onSelectAccount={setAccount}
            autoFocus
            // Venmo/EZPass rows are always USD; a foreign destination would
            // only be rejected by the server's currency guard.
            currency="USD"
          />
        </InputGroup>
        {importType === 'venmo' && <ImportVenmo onChange={handleVenmoChange} />}
        {importType === 'ezpass' && (
          <ImportEzpass onChange={handleEzpassChange} />
        )}
      </div>
    </Modal>
  );
};
